/* eslint-disable react/jsx-one-expression-per-line */
import React from 'react'
import { useRecoilValue } from 'recoil'
import Head from 'next/head'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import clsx from 'clsx'

import {
  GetVenueDetailDocument,
  GetVenueDetailQuery,
  GetVenueSlugsDocument,
  GetVenueSlugsQuery,
  Locale,
} from 'generated/graphcms'

import Layout from 'components/layout/Layout'
import Container from 'components/ui/Container/Container'
import IconChevronLeft from 'components/ui/Icon/IconChevronLeft'
import VenueDetails from 'components/shared/Slugs/VenueDetails'
import { FooterData, getFooterData } from 'components/layout/AppShell/getFooterData'
import { APP_ROUTES, STATIC_FETCH_POLICY } from 'utils/constants'
import { graphcms } from 'utils/graphcms'
import { localeParamFor } from 'utils/localisation'
import lastSearchPath from 'store/searchFilters'

interface Props {
  locale: Locale
  venue: GetVenueDetailQuery['venue']
  footerData: FooterData
}

export async function getStaticPaths({ locales }: { locales: Locale[] }) {
  const {
    data: { venues = [] },
  } = await graphcms.query<GetVenueSlugsQuery>({
    query: GetVenueSlugsDocument,
    variables: { locales },
  })

  const paths = venues
    .map((venue) => locales.map((locale) => ({ params: { slug: venue.slug }, locale })))
    .flat()

  return {
    paths,
    fallback: false,
  }
}

export async function getStaticProps({
  params,
  locale,
}: {
  params: { slug: string }
  locale: Locale
}) {
  const { data } = await graphcms.query<GetVenueDetailQuery>({
    query: GetVenueDetailDocument,
    fetchPolicy: STATIC_FETCH_POLICY,
    variables: {
      where: {
        slug: params.slug,
      },
      locales: localeParamFor(locale),
    },
  })

  return {
    props: {
      locale,
      venue: data.venue,
      footerData: await getFooterData(locale),
      ...(await serverSideTranslations(locale, ['common'])),
    },
  }
}

const VenuePage: React.FC<Props> = ({ locale, venue, footerData }) => {
  const { t } = useTranslation()
  const theLastSearchPath = useRecoilValue(lastSearchPath)

  if (!venue) {
    return <div>Loading..</div>
  }

  const locationLinkSuffix = footerData.areas.length === 1 ? `/${footerData.areas[0].slug}` : ''

  const metaTitle = t('siteTitleWrapper', { title: venue?.title })

  return (
    <Layout footerData={footerData}>
      <Head>
        <title>{metaTitle}</title>
        <meta property="og:title" content={metaTitle} />
        {!!venue.gallery[0] && (
          <meta property="og:image" key="ogImage" content={venue.gallery[0].url1200_630} />
        )}

        {venue.metaDescription && (
          <>
            <meta name="description" content={venue.metaDescription} />
            <meta property="og:description" content={venue.metaDescription} />
          </>
        )}
      </Head>
      <Container>
        <div className="lg:px-[51px]">
          <div className="flex justify-between">
            <div className="relative flex flex-row-reverse lg:block">
              <div>
                <h1 className="text-lg font-semibold md:text-3xl">{venue.title}</h1>
                <p className="text-sm text-gray-500 md:pt-2 md:text-base">
                  {`${venue.area?.title}, ${venue.country?.title}`}
                </p>
              </div>

              <Link
                className={clsx(
                  '-top-0.5 left-0 mr-5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-100',
                  'transition-colors duration-200 ease-in-out',
                  ' md:h-10 md:w-10',
                  'lg:absolute lg:mr-0 lg:-translate-x-[calc(100%+20px)] lg:transform',
                  'hover:bg-gray-200'
                )}
                href={theLastSearchPath || APP_ROUTES.VENUES + locationLinkSuffix}
              >
                <IconChevronLeft size="20" />
              </Link>
            </div>
          </div>

          <VenueDetails locale={locale} venue={venue} />
        </div>
      </Container>
    </Layout>
  )
}

export default VenuePage
